import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { X, Save, AlertCircle, Loader2, ImageIcon, Upload, FileText, Plus, Trash2 } from 'lucide-react';
import useMuscleGroups from '../../hooks/useMuscleGroups';

const ExerciseForm = ({ exercise = null, onExerciseAdded, onClose, allowImageEdit = true, imageInfoMessage = '' }) => {
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();
    const { muscles, loading: musclesLoading, getMusclesByCategory } = useMuscleGroups();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [availableImages, setAvailableImages] = useState([]);
    const [imageInputMode, setImageInputMode] = useState('manual'); // 'manual' o 'select'
    const [selectedCategory, setSelectedCategory] = useState('');
    const [secondaryMuscles, setSecondaryMuscles] = useState([]);
    
    const [formData, setFormData] = useState({
        nome: exercise ? exercise.nome : '',
        nome_en: exercise ? exercise.nome_en : '', // WEBAPP-005: Nome in inglese
        descrizione: exercise ? exercise.descrizione : '',
        descrizione_en: exercise ? exercise.descrizione_en : '', // WEBAPP-005: Descrizione in inglese
        gruppo_muscolare: exercise ? exercise.gruppo_muscolare : '',
        primary_muscle_id: exercise ? exercise.primary_muscle_id : null,
        attrezzatura: exercise ? exercise.attrezzatura : '',
        immagine_nome: exercise ? exercise.immagine_nome || exercise.immagine_statica || '' : '',
        immagine_statica: exercise ? exercise.immagine_statica : '', // WEBAPP-005: Immagine statica separata
        is_cardio: exercise ? !!(exercise.is_cardio || exercise.is_isometric) : false, // Retrocompatibilità: supporta anche is_isometric
        equipment_type_id: exercise ? exercise.equipment_type_id : null
    });

    const [imagePreview, setImagePreview] = useState('');
    const [imageError, setImageError] = useState(false);

    // Categorie muscolari
    const categories = ['Gambe', 'Petto', 'Schiena', 'Spalle', 'Braccia', 'Core', 'Cardio'];

    // Aggiorna formData quando exercise cambia
    useEffect(() => {
        if (exercise) {
            setFormData({
                nome: exercise.nome || '',
                nome_en: exercise.nome_en || '', // WEBAPP-005: Nome in inglese
                descrizione: exercise.descrizione || '',
                descrizione_en: exercise.descrizione_en || '', // WEBAPP-005: Descrizione in inglese
                gruppo_muscolare: exercise.gruppo_muscolare || '',
                primary_muscle_id: exercise.primary_muscle_id || null,
                attrezzatura: exercise.attrezzatura || '',
                immagine_nome: exercise.immagine_nome || exercise.immagine_statica || exercise.immagine_url || '', // Retrocompatibilità
                immagine_statica: exercise.immagine_statica || '', // WEBAPP-005: Immagine statica separata
                is_cardio: !!(exercise.is_cardio || exercise.is_isometric), // Retrocompatibilità: supporta anche is_isometric
                equipment_type_id: exercise.equipment_type_id || null
            });
        }
    }, [exercise]);

    useEffect(() => {
        const fetchAvailableImages = async () => {
            if (!allowImageEdit) {
                setAvailableImages([]);
                return;
            }

            try {
                const response = await api.get('/available_images.php');
                if (response.data && response.data.images) {
                    setAvailableImages(response.data.images);
                }
            } catch (err) {
                console.error('Errore nel caricamento delle immagini disponibili:', err);
            }
        };

        fetchAvailableImages();

        // Inizializza muscoli secondari se in modifica
        if (exercise && exercise.secondary_muscles) {
            setSecondaryMuscles(exercise.secondary_muscles.map(sm => ({
                muscle_id: sm.id,
                activation_level: sm.activation_level || 'high'
            })));
        } else {
            setSecondaryMuscles([]);
        }

        // Inizializza categoria se presente muscolo primario
        if (exercise && exercise.primary_muscle) {
            setSelectedCategory(exercise.primary_muscle.category);
        } else if (exercise && exercise.gruppo_muscolare) {
            setSelectedCategory(exercise.gruppo_muscolare);
        } else {
            setSelectedCategory('');
        }
    }, [api, exercise, allowImageEdit]);

    // Aggiorna l'anteprima quando cambia il nome dell'immagine
    useEffect(() => {
        const nomeGif = (formData.immagine_nome || '').trim();
        if (nomeGif) {
            const imageUrl = `/api/serve_image.php?filename=${encodeURIComponent(nomeGif)}`;
            setImagePreview(imageUrl);
        } else {
            setImagePreview('');
        }
    }, [formData.immagine_nome]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Gestisci checkbox per is_cardio
        if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked ? 1 : 0 });
            return;
        }
        
        setFormData({ ...formData, [name]: value });
    };

    const handleImageSelect = (imageName) => {
        setFormData({ ...formData, immagine_nome: imageName });
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setFormData({ ...formData, primary_muscle_id: null });
    };

    const handlePrimaryMuscleChange = (muscleId) => {
        const muscle = muscles.find(m => m.id === parseInt(muscleId));
        setFormData({
            ...formData,
            primary_muscle_id: muscleId ? parseInt(muscleId) : null,
            gruppo_muscolare: muscle ? muscle.parent_category : ''
        });
    };

    const handleAddSecondaryMuscle = () => {
        setSecondaryMuscles([...secondaryMuscles, { muscle_id: null, activation_level: 'high' }]);
    };

    const handleRemoveSecondaryMuscle = (index) => {
        setSecondaryMuscles(secondaryMuscles.filter((_, i) => i !== index));
    };

    const handleSecondaryMuscleChange = (index, field, value) => {
        const updated = [...secondaryMuscles];
        updated[index][field] = field === 'muscle_id' ? parseInt(value) : value;
        setSecondaryMuscles(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validazione dei campi obbligatori
        if (!formData.nome) {
            setError('Il nome è obbligatorio');
            return;
        }
        
        if (!formData.primary_muscle_id) {
            setError('Il muscolo primario è obbligatorio');
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            
            // Prepara i dati per l'invio
            const dataToSend = {
                ...formData,
                nome_en: formData.nome_en || '', // WEBAPP-005: Includi nome inglese
                descrizione_en: formData.descrizione_en || '', // WEBAPP-005: Includi descrizione inglese
                immagine_statica: formData.immagine_statica || '', // WEBAPP-005: Includi immagine statica
                is_cardio: formData.is_cardio ? 1 : 0,
                equipment_type_id: formData.equipment_type_id || null,
                secondary_muscles: secondaryMuscles
                    .filter(sm => sm.muscle_id && sm.muscle_id !== formData.primary_muscle_id)
                    .map(sm => ({
                        muscle_id: sm.muscle_id,
                        activation_level: sm.activation_level
                    }))
            };
            
            let response;
            if (exercise) {
                // Aggiornamento esercizio esistente
                response = await api.put(`/esercizi.php?id=${exercise.id}`, dataToSend);
            } else {
                // Creazione nuovo esercizio
                response = await api.post('/esercizi.php', dataToSend);
            }
            
            if (response.data) {
                onExerciseAdded(response.data);
            }
        } catch (err) {
            console.error('Errore durante il salvataggio:', err);
            setError(err.response?.data?.message || 'Errore durante il salvataggio');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {exercise ? 'Modifica Esercizio' : 'Nuovo Esercizio'}
                </h2>
                
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-white/65 dark:hover:text-gray-200"
                    >
                        <X className="h-6 w-6" />
                    </button>
                )}
            </div>
            
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    <p>{error}</p>
                </div>
            )}
            
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Colonna sinistra */}
                    <div className="space-y-6">
                        {/* Nome */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Nome Esercizio (Italiano)*
                            </label>
                            <input
                                type="text"
                                name="nome"
                                value={formData.nome}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                placeholder="es. Panca Piana"
                                required
                            />
                        </div>
                        
                        {/* WEBAPP-005: Nome Inglese */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Nome Esercizio (Inglese)
                            </label>
                            <input
                                type="text"
                                name="nome_en"
                                value={formData.nome_en}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                placeholder="es. Bench Press"
                            />
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                                Traduzione inglese del nome (opzionale, per utenti internazionali)
                            </p>
                        </div>
                        
                        {/* Categoria Muscolare */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Categoria Muscolare*
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                required
                            >
                                <option value="">Seleziona categoria</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {/* Muscolo Primario */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Muscolo Primario*
                            </label>
                            <select
                                value={formData.primary_muscle_id || ''}
                                onChange={(e) => handlePrimaryMuscleChange(e.target.value)}
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                disabled={!selectedCategory || musclesLoading}
                                required
                            >
                                <option value="">
                                    {musclesLoading ? 'Caricamento...' : 'Seleziona muscolo primario'}
                                </option>
                                {selectedCategory && getMusclesByCategory(selectedCategory).map(muscle => (
                                    <option key={muscle.id} value={muscle.id}>
                                        {muscle.name}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                                Il muscolo principale allenato dall'esercizio
                            </p>
                        </div>

                        {/* Muscoli Secondari */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-white/85">
                                    Muscoli Secondari (opzionale)
                                </label>
                                <button
                                    type="button"
                                    onClick={handleAddSecondaryMuscle}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Aggiungi
                                </button>
                            </div>
                            
                            {secondaryMuscles.length > 0 ? (
                                <div className="space-y-2">
                                    {secondaryMuscles.map((sm, index) => (
                                        <div key={index} className="flex gap-2 items-start">
                                            <select
                                                value={sm.muscle_id || ''}
                                                onChange={(e) => handleSecondaryMuscleChange(index, 'muscle_id', e.target.value)}
                                                className="flex-1 p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                <option value="">Seleziona muscolo</option>
                                                {muscles
                                                    .filter(m => m.id !== formData.primary_muscle_id)
                                                    .map(muscle => (
                                                        <option key={muscle.id} value={muscle.id}>
                                                            {muscle.name} ({muscle.parent_category})
                                                        </option>
                                                    ))}
                                            </select>
                                            <select
                                                value={sm.activation_level}
                                                onChange={(e) => handleSecondaryMuscleChange(index, 'activation_level', e.target.value)}
                                                className="w-28 p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                <option value="high">Alta</option>
                                                <option value="medium">Media</option>
                                                <option value="low">Bassa</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSecondaryMuscle(index)}
                                                className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-white/65 italic">
                                    Nessun muscolo secondario aggiunto
                                </p>
                            )}
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                                Muscoli che vengono attivati secondariamente durante l'esercizio
                            </p>
                        </div>
                        
                        {/* Attrezzatura */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Attrezzatura
                            </label>
                            <input
                                type="text"
                                name="attrezzatura"
                                value={formData.attrezzatura}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                placeholder="es. Bilanciere, Manubri, Cavi, ecc."
                            />
                        </div>
                        
                        {/* Esercizio Cardio */}
                        <div>
                            <label className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id="is_cardio"
                                    name="is_cardio"
                                    checked={!!formData.is_cardio}
                                    onChange={e => setFormData({ ...formData, is_cardio: e.target.checked ? 1 : 0 })}
                                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-gray-700 dark:text-white/85">Esercizio Cardio</span>
                            </label>
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/65 ml-7">
                                Gli esercizi cardio sono attività aerobiche (es. corsa, cyclette, tapis roulant)
                            </p>
                        </div>
                    </div>
                    
                    {/* Colonna destra */}
                    <div className="space-y-6">
                        {/* Descrizione */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Descrizione (Italiano)
                            </label>
                            <textarea
                                name="descrizione"
                                value={formData.descrizione}
                                onChange={handleInputChange}
                                rows="4"
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                placeholder="Descrivi l'esecuzione corretta dell'esercizio..."
                            />
                        </div>
                        
                        {/* WEBAPP-005: Descrizione Inglese */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Descrizione (Inglese)
                            </label>
                            <textarea
                                name="descrizione_en"
                                value={formData.descrizione_en}
                                onChange={handleInputChange}
                                rows="4"
                                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                placeholder="Describe the correct execution..."
                            />
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                                Traduzione inglese della descrizione (opzionale)
                            </p>
                        </div>
                        
                        {/* Nome Immagine */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Immagine GIF
                            </label>

                            {allowImageEdit ? (
                                <>
                                    {/* Toggle tra modalità manuale e selezione */}
                                    <div className="flex space-x-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('manual')}
                                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                                imageInputMode === 'manual'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-white/85'
                                            }`}
                                        >
                                            <FileText className="h-4 w-4 inline mr-1" />
                                            Inserisci Nome
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('select')}
                                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                                imageInputMode === 'select'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-white/85'
                                            }`}
                                        >
                                            <Upload className="h-4 w-4 inline mr-1" />
                                            Seleziona File
                                        </button>
                                    </div>

                                    {imageInputMode === 'manual' ? (
                                        <input
                                            type="text"
                                            name="immagine_nome"
                                            value={formData.immagine_nome}
                                            onChange={handleInputChange}
                                            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                            placeholder="es. panca_piana.gif"
                                        />
                                    ) : (
                                        <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50 dark:bg-gray-800">
                                            {availableImages.length > 0 ? (
                                                <div className="grid grid-cols-2 gap-2">
                                                    {availableImages.map((image, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            onClick={() => handleImageSelect(image)}
                                                            className={`p-2 text-left text-sm rounded border transition-colors ${
                                                                formData.immagine_nome === image
                                                                    ? 'bg-blue-100 dark:bg-blue-900 border-blue-300 dark:border-blue-600'
                                                                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                            }`}
                                                        >
                                                            {image}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 dark:text-white/65 text-sm">
                                                    Nessuna immagine disponibile
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                                        Inserisci il nome del file GIF (es. panca_piana.gif) o seleziona da quelli disponibili
                                    </p>
                                </>
                            ) : (
                                <div className="p-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/40 text-sm text-gray-600 dark:text-white/85">
                                    <p className="font-medium">
                                        {formData.immagine_nome ? (
                                            <>
                                                File associato: <code className="text-xs bg-gray-200 dark:bg-gray-900 px-2 py-0.5 rounded">{formData.immagine_nome}</code>
                                            </>
                                        ) : (
                                            'Nessuna immagine associata'
                                        )}
                                    </p>
                                    {imageInfoMessage && (
                                        <p className="mt-2 text-xs text-gray-500 dark:text-white/65">
                                            {imageInfoMessage}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        {/* Anteprima Immagine */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Anteprima
                            </label>
                            <div className="w-full h-36 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
                                {imagePreview && !imageError ? (
                                    <img
                                        src={imagePreview}
                                        alt="Anteprima esercizio"
                                        className="max-w-full max-h-full object-contain"
                                        onError={e => { e.target.onerror = null; setImageError(true); }}
                                        onLoad={() => setImageError(false)}
                                    />
                                ) : imageError ? (
                                    <div className="text-red-500 flex flex-col items-center">
                                        <ImageIcon className="h-8 w-8 mb-1" />
                                        <span className="text-sm">Immagine non trovata</span>
                                    </div>
                                ) : (
                                    <div className="text-gray-400 dark:text-gray-500 flex flex-col items-center">
                                        <ImageIcon className="h-8 w-8 mb-1" />
                                        <span className="text-sm">Nessuna immagine</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Pulsanti azioni */}
                <div className="flex justify-end space-x-3 mt-8">
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-white/85 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            Annulla
                        </button>
                    )}
                    
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:bg-blue-400 dark:disabled:bg-blue-800 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                <span>Salvataggio...</span>
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                <span>{exercise ? 'Aggiorna' : 'Salva'}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ExerciseForm;